/** Period and resolution picker (§4.1: today / yesterday / week / month / year). */

import { useTranslation } from 'react-i18next';

import type { Resolution } from '../api/types';

export type RangeKey = 'today' | 'yesterday' | 'week' | 'month' | 'year';

export const RANGE_RESOLUTION: Record<RangeKey, Resolution> = {
  today: '5min',
  yesterday: '5min',
  week: '15min',
  month: 'hour',
  year: 'day',
};

export function rangeBounds(key: RangeKey): { from: string; to: string } {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  switch (key) {
    case 'today':
      return { from: startOfToday.toISOString(), to: now.toISOString() };
    case 'yesterday': {
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - 1);
      return { from: start.toISOString(), to: startOfToday.toISOString() };
    }
    case 'week': {
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - 6);
      return { from: start.toISOString(), to: now.toISOString() };
    }
    case 'month': {
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - 29);
      return { from: start.toISOString(), to: now.toISOString() };
    }
    case 'year': {
      const start = new Date(startOfToday);
      start.setFullYear(start.getFullYear() - 1);
      return { from: start.toISOString(), to: now.toISOString() };
    }
  }
}

const RANGES: RangeKey[] = ['today', 'yesterday', 'week', 'month', 'year'];
const RESOLUTIONS: Resolution[] = ['raw', '5min', '15min', 'hour', 'day'];

interface Props {
  range: RangeKey;
  resolution: Resolution;
  onRangeChange: (range: RangeKey) => void;
  onResolutionChange: (resolution: Resolution) => void;
}

export function TimeRange({ range, resolution, onRangeChange, onResolutionChange }: Props) {
  const { t } = useTranslation();
  return (
    <div className="toolbar">
      <div className="segmented" role="group" aria-label={t('chart.range')}>
        {RANGES.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={range === key}
            className={range === key ? 'is-active' : ''}
            onClick={() => {
              onRangeChange(key);
              onResolutionChange(RANGE_RESOLUTION[key]);
            }}
          >
            {t(`chart.ranges.${key}`)}
          </button>
        ))}
      </div>
      <label className="field field--inline">
        <span>{t('chart.resolution')}</span>
        <select
          value={resolution}
          onChange={(event) => onResolutionChange(event.target.value as Resolution)}
        >
          {RESOLUTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`chart.resolutions.${value}`)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
